import React from "react";
import { Navigate, RouteObject } from "react-router-dom";
import MainLayout from "./layouts/main-layout/MainLayout";
import LandingPage from "./LandingPage";

import researchProfileRoutes from "./modules/research-profile/routes";
import clinicalTrialRoutes from "./modules/clinical-trial/routes";
import eLearningRoutes from "./modules/e-learning/routes";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faFlask,
  faGraduationCap,
  faNotesMedical,
} from "@fortawesome/free-solid-svg-icons";
import GuestLayout from "./layouts/guest-layout/GuestLayout";
import guestRoutes from "./guest/routes";
import AfterLoginGuard from "./guards/AfterLoginGuard";
import { useAuth } from "./context/AuthContext";
import NotFound from "./guest/NotFound";

export type CustomRouteObject = Omit<RouteObject, "index"> & {
  name?: string;
  icon?: string;
  bgIcon?: IconProp;
  permissions?: string[];
};

// ProtectedRoute component
const ProtectedRoute: React.FC<{
  element: React.ReactNode;
  permissions?: string[] | string;
}> = ({ element, permissions }) => {
  const authService = useAuth();

  if (permissions) {
    const requiredPermissions = Array.isArray(permissions)
      ? permissions
      : [permissions];
    if (!authService.hasPermission(requiredPermissions)) {
      return authService.isAuthenticated() ? (
        <Navigate to="/access-denied" replace />
      ) : (
        <Navigate to="/login" replace />
      );
    }
  }

  return <>{element}</>;
};

// Utility function to add metadata only to valid non-index routes
// Mfano:
// children: [
//     { index: true, element: <DashboardHome /> }, // Default child route
//     { path: "settings", element: <DashboardSettings /> }
// ]
const addMetadataToRoutes = (
  routes: RouteObject[],
  metadata: Partial<CustomRouteObject>
): CustomRouteObject[] =>
  routes
    .filter((route) => !("index" in route))
    .map((route: CustomRouteObject) => {
      return {
        ...route,
        ...metadata,
        element: (
          <ProtectedRoute
            element={route.element}
            permissions={
              (metadata as CustomRouteObject).permissions || route.permissions
            }
          />
        ),
      };
    });

// Add metadata for modules( name, icon and permission)
const clinicalTrialRoutesWithMetadata = addMetadataToRoutes(
  clinicalTrialRoutes,
  {
    name: "Clinical Trials",
    icon: "medical_services",
    bgIcon: faNotesMedical,
    permissions: ["/auth/register"],
  }
);

const eLearningRoutesWithMetadata = addMetadataToRoutes(eLearningRoutes, {
  name: "E-Learning",
  icon: "school",
  bgIcon: faGraduationCap,
  permissions: ["/auth/register"],
});

const researchProfileRoutesWithMetadata = addMetadataToRoutes(
  researchProfileRoutes,
  {
    name: "Research Profile",
    icon: "science",
    bgIcon: faFlask,
    permissions: ["/auth/home/some"],
  }
);

// Combined metadata routes
const metadataRoutes: CustomRouteObject[] = [
  ...clinicalTrialRoutesWithMetadata,
  ...eLearningRoutesWithMetadata,
  ...researchProfileRoutesWithMetadata,
];

const routes: CustomRouteObject[] = [
  {
    path: "/",
    element: <AfterLoginGuard />,
    children: [
      {
        path: "",
        element: <LandingPage />,
      },
    ],
  },
  {
    path: "/",
    element: <MainLayout />,
    children: [...metadataRoutes],
  },
  {
    path: "",
    element: <GuestLayout />,
    children: [...guestRoutes],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
